import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useRef, useState } from "react";
import { CaseCreation } from "types";
import {
  buildChargeSheet,
  fullDocument,
  SheetLanguage,
  SHEET_CSS,
} from "./chargeSheetTemplate";

/**
 * Charge sheet editor: the sheet is generated from the charge's registered
 * data, edited in place like a document, signed with the prosecutor's
 * signature image, then printed (or saved as PDF from the print dialog),
 * downloaded as a Word file, or attached as the charge draft.
 *
 * Edits are kept in this browser per case until the sheet is regenerated.
 */
type Props = {
  caseData: CaseCreation;
  prosecutorName?: string;
  /** Given only to whoever may upload the charge draft. */
  onUseAsDraft?: (file: File) => void;
  onClose: () => void;
};

const TEXT: Record<SheetLanguage, Record<string, string>> = {
  en: {
    heading: "Charge sheet",
    hint: "Click anywhere on the page to edit. Highlighted [ ... ] items are missing data.",
    language: "Sheet language",
    regenerate: "Regenerate from data",
    regenerateConfirm: "Regenerate the sheet from the registered data? Your edits will be lost.",
    sign: "Add signature",
    removeSign: "Remove signature",
    print: "Print / PDF",
    word: "Download Word",
    useDraft: "Use as charge draft",
    usedDraft: "Attached as the charge draft. Forward it to the coordinator to submit.",
    close: "Close",
    badImage: "Choose a PNG or JPG image of the signature.",
  },
  am: {
    heading: "የክስ ቻርጅ",
    hint: "ለማስተካከል በገጹ ላይ ይጫኑ። ደምቀው የሚታዩ [ ... ] ያልተሞሉ መረጃዎች ናቸው።",
    language: "የሰነዱ ቋንቋ",
    regenerate: "ከመረጃው እንደገና አዘጋጅ",
    regenerateConfirm: "ሰነዱ ከተመዘገበው መረጃ እንደገና ይዘጋጅ? ያደረጉት ማስተካከያ ይጠፋል።",
    sign: "ፊርማ አስገባ",
    removeSign: "ፊርማ አስወግድ",
    print: "አትም / PDF",
    word: "Word አውርድ",
    useDraft: "እንደ ክስ ረቂቅ ተጠቀም",
    usedDraft: "እንደ ክስ ረቂቅ ተያይዟል። ለማቅረብ ወደ አስተባባሪ ያስተላልፉ።",
    close: "ዝጋ",
    badImage: "የፊርማውን PNG ወይም JPG ምስል ይምረጡ።",
  },
  or: {
    heading: "Waraqaa himannaa",
    hint: "Gulaaluuf fuula irratti cuqaasaa. [ ... ] kan calaqqisan odeeffannoo hin guutamneedha.",
    language: "Afaan waraqaa",
    regenerate: "Odeeffannoo irraa haaraa qopheessi",
    regenerateConfirm: "Waraqaan odeeffannoo galmaa'e irraa haaraa haa qophaa'u? Sirreeffamni keessan ni bada.",
    sign: "Mallattoo galchi",
    removeSign: "Mallattoo haqi",
    print: "Maxxansi / PDF",
    word: "Word buusi",
    useDraft: "Akka wixinee himannaatti fayyadami",
    usedDraft: "Akka wixinee himannaatti maxxanfameera. Dhiyeessuuf gara qindeessaatti dabarsaa.",
    close: "Cufi",
    badImage: "Suuraa mallattoo PNG ykn JPG filadhaa.",
  },
};

const currentLanguage = (): SheetLanguage => {
  try {
    const lang = (localStorage.getItem("language") || "or").slice(0, 2);
    return lang === "am" || lang === "en" ? lang : "or";
  } catch {
    return "or";
  }
};

const storageKey = (caseId: number | string) => `charge-sheet:${caseId}`;

const readSaved = (caseId: number | string) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(caseId)) || "null") as
      | { html: string; language: SheetLanguage }
      | null;
  } catch {
    return null;
  }
};

const ChargeSheetEditor: React.FC<Props> = ({
  caseData,
  prosecutorName,
  onUseAsDraft,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const pageRef = useRef<HTMLDivElement>(null);
  const signInputRef = useRef<HTMLInputElement>(null);
  const saved = readSaved(caseData.id);
  const [language, setLanguage] = useState<SheetLanguage>(
    saved?.language || currentLanguage(),
  );
  const [hasSignature, setHasSignature] = useState(false);
  const text = TEXT[language];
  const title = `Charge ${caseData.prosecutor_registration_number || caseData.id}`;

  const generate = (lang: SheetLanguage) =>
    buildChargeSheet({ caseData, prosecutorName, language: lang });

  const save = () => {
    if (!pageRef.current) return;
    setHasSignature(!!pageRef.current.querySelector(".cs-signature"));
    try {
      localStorage.setItem(
        storageKey(caseData.id),
        JSON.stringify({ html: pageRef.current.innerHTML, language }),
      );
    } catch {
      // storage full or blocked: the sheet still works for this session
    }
  };

  useEffect(() => {
    if (!pageRef.current) return;
    pageRef.current.innerHTML = saved?.html || generate(language);
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regenerate = (lang = language) => {
    if (!pageRef.current) return;
    pageRef.current.innerHTML = generate(lang);
    save();
  };

  const changeLanguage = (lang: SheetLanguage) => {
    if (!window.confirm(TEXT[lang].regenerateConfirm)) return;
    setLanguage(lang);
    regenerate(lang);
  };

  const format = (command: string) => {
    pageRef.current?.focus();
    document.execCommand(command);
    save();
  };

  // The prosecutor browses for an image of their signature; it is placed on
  // the signature line, replacing the blank line or an earlier signature.
  const addSignature = (file?: File | null) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
      showTooltip(text.badImage, "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const page = pageRef.current;
      const cell = page?.querySelectorAll(".cs-sign td")[1];
      if (!page || !cell) return;
      const label = (cell.textContent || "").split(":")[0];
      cell.innerHTML = `${label}: <img class="cs-signature" src="${reader.result}" alt="signature" style="height:60px;vertical-align:middle" />`;
      save();
    };
    reader.readAsDataURL(file);
  };

  const removeSignature = () => {
    const img = pageRef.current?.querySelector(".cs-signature");
    if (!img) return;
    img.replaceWith(document.createTextNode("________________"));
    save();
  };

  const html = () => fullDocument(pageRef.current?.innerHTML || "", title);

  const print = () => {
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    win.document.open();
    win.document.write(html());
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
    };
  };

  // Word opens HTML saved with a .doc name, keeping the layout and images.
  const wordFile = () =>
    new File(["﻿", html()], `${title.replace(/[^\w.-]+/g, "_")}.doc`, {
      type: "application/msword",
    });

  const downloadWord = () => {
    const url = URL.createObjectURL(wordFile());
    const a = document.createElement("a");
    a.href = url;
    a.download = wordFile().name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const useAsDraft = () => {
    if (!onUseAsDraft) return;
    onUseAsDraft(wordFile());
    showTooltip(text.usedDraft, "success");
    onClose();
  };

  const toolButton =
    "px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50";

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100">
      <style>{SHEET_CSS}</style>
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-3 shadow-sm">
        <h2 className="mr-auto text-lg font-semibold text-gray-900">
          {text.heading}
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          {text.language}
          <select
            value={language}
            onChange={(e) => changeLanguage(e.target.value as SheetLanguage)}
            className="rounded-md border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="or">Afaan Oromoo</option>
            <option value="am">አማርኛ</option>
            <option value="en">English</option>
          </select>
        </label>
        <button type="button" className={`${toolButton} font-bold`} onClick={() => format("bold")}>
          B
        </button>
        <button type="button" className={`${toolButton} italic`} onClick={() => format("italic")}>
          I
        </button>
        <button type="button" className={`${toolButton} underline`} onClick={() => format("underline")}>
          U
        </button>
        <button
          type="button"
          className={toolButton}
          onClick={() => window.confirm(text.regenerateConfirm) && regenerate()}
        >
          {text.regenerate}
        </button>
        <button type="button" className={toolButton} onClick={() => signInputRef.current?.click()}>
          ✍️ {text.sign}
        </button>
        {hasSignature && (
          <button type="button" className={toolButton} onClick={removeSignature}>
            {text.removeSign}
          </button>
        )}
        <input
          ref={signInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={(e) => {
            addSignature(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <button type="button" className={toolButton} onClick={print}>
          🖨️ {text.print}
        </button>
        <button type="button" className={toolButton} onClick={downloadWord}>
          {text.word}
        </button>
        {onUseAsDraft && (
          <button
            type="button"
            onClick={useAsDraft}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700"
          >
            {text.useDraft}
          </button>
        )}
        <button type="button" className={toolButton} onClick={onClose}>
          {text.close}
        </button>
      </div>
      <p className="px-4 pt-3 text-center text-xs text-gray-500">{text.hint}</p>
      <div className="flex-1 overflow-auto p-4">
        <div
          ref={pageRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onInput={save}
          className="cs-page mx-auto min-h-[29.7cm] w-full max-w-[21cm] bg-white px-6 py-8 shadow-lg outline-none sm:px-[2cm] sm:py-[2.2cm]"
        />
      </div>
    </div>
  );
};

export default ChargeSheetEditor;
